import {ParticipantNotificationSettings} from './ParticipantNotificationSettings';
import * as React from 'react';
import { parse } from 'query-string';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { UnsavedChangesModal } from 'Src/components/UnsavedChangesModal/UnsavedChangesModal';
import {
  getParticipationSummary,
  participantRoot,
  participantPages,
  participantDestination
} from 'Src/helpers/tournament/participation';
import { errorMessage, formatLondonDate } from 'Src/helpers/service/adminSU/highLevelTournament';
import { isIndividualTournament } from 'Src/helpers/tournament/tournament';
import { unsubscribeTournament } from 'Src/helpers/service/adminSU/tournamentsTable';
import {
  getMessageDetails,
  acceptConfirmationRequest,
  rejectConfirmationRequest
} from 'Src/helpers/service/admin/messages';
import { ConfirmationRequestForm } from '../AdminView/Messages/ConfirmationRequestForm/ConfirmationRequestForm';
import { formatAgesCompact, resolveAgeNamingFromContext } from 'Src/helpers/ageGroups';
import { TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/tournament';
import { TournamentTeams } from '../AdminView/Tournaments/TournamentTeams';
import { TournamentTeamPlayersManager } from '../AdminView/Tournaments/TournamentTeamPlayersManager';
import { TournamentTeamPlayers } from '../AdminView/Tournaments/TournamentTeamPlayers/TournamentTeamPlayers';
import { TournamentEvents } from '../AdminView/Tournaments/TournamentEvents';
import { HighLevelTournamentTeams } from '../AdminSUView/HighLevelTournaments/HighLevelTournamentTeams/HighLevelTournamentTeams';
import { HighLevelTournamentTeamPlayersEdit } from '../AdminSUView/HighLevelTournaments/HighLevelTournamentTeamPlayersEdit/HighLevelTournamentTeamPlayersEdit';
import { HighLevelTournamentTeamPlayers } from '../AdminSUView/HighLevelTournaments/HighLevelTournamentTeamPlayers/HighLevelTournamentTeamPlayers';
import { HighLevelTournamentEvents } from '../AdminSUView/HighLevelTournaments/HighLevelTournamentEvents/HighLevelTournamentEvents';
import { TournamentAvailability } from '../AdminSUView/HighLevelTournaments/TournamentAvailability/TournamentAvailability';
import { RepresentedSchools } from '../AdminSUView/HighLevelTournaments/TournamentAvailability/RepresentedSchools';
import { TournamentSchoolNotificationComposer } from '../AdminSUView/Tournaments/TournamentManagement/TournamentSchoolNotificationComposer';
import { TournamentNotifications } from '../AdminSUView/Tournaments/TournamentNotifications';
import { TournamentCommunications } from '../AdminSUView/Tournaments/TournamentManagement/TournamentCommunications';
import { AdditionalInfo } from '../AdminView/Tournaments/TournamentsUpcoming/TournamentDetails/AdditionalInfo/AdditionalInfo';
import { DetailsAndBooking } from '../AdminView/Tournaments/TournamentsUpcoming/TournamentDetails/DetailsAndBooking/DetailsAndBooking';
import { ParticipantReports } from './ParticipantReports';
import { ParticipationLimits } from './ParticipationLimits';
import { TournamentRecruitmentPage } from '../AdminSUView/HighLevelTournaments/TournamentRecruitment/TournamentRecruitment';
import '../AdminSUView/Tournaments/TournamentManagement/TournamentManagement.scss';
import '../AdminView/Tournaments/TournamentsHub/TournamentsHub.css';
import './TournamentParticipation.scss';

export function TournamentParticipation({
  user,
  history,
  location,
  match,
  union = false
}: {
  user: AppUser;
  history: any;
  location: any;
  match: any;
  union?: boolean;
}) {
  const tournamentId = String(parse(location.search).tournamentId || '');
  const root = participantRoot(union);
  const page = location.pathname.slice(root.length).replace(/^\//, '') || 'overview';
  const section = page.split('/')[0];
  const [data, setData] = React.useState<any>(null);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [reports, setReports] = React.useState(false);
  const [notificationSettingsOpen, setNotificationSettingsOpen] = React.useState(false);
  const [teamMemberCounts, setTeamMemberCounts] = React.useState<Record<string, number>>({});
  const onTeamMemberCountChange = React.useCallback((teamId: string, count: number) => {
    setTeamMemberCounts(counts => counts[teamId] === count ? counts : {...counts, [teamId]: count});
  }, []);
  const [withdraw, setWithdraw] = React.useState(false);
  const [leave, setLeave] = React.useState<any>(null);
  const [mapVisible, setMapVisible] = React.useState(false);
  const [confirmation, setConfirmation] = React.useState<any>(null);
  const [confirmationDirty, setConfirmationDirty] = React.useState(false);
  const [declineConfirmation, setDeclineConfirmation] = React.useState(false);
  const confirmationDirtyRef = React.useRef(false);
  confirmationDirtyRef.current = confirmationDirty;
  const editor = React.useRef<any>(null);
  const generation = React.useRef(0);
  const unblock = React.useRef<any>(null);
  const returnState = location.state || {};
  const listPath =
    returnState.participationReturnPath || (union ? '/highLevelTournaments/upcoming' : '/tournaments/upcoming');
  const goBack = () =>
    history.push({
      pathname: listPath,
      search: returnState.participationReturnSearch || '',
      state: returnState.participationListState
    });
  const load = async () => {
    const currentGeneration = ++generation.current;
    setBusy(true);
    setError('');
    try {
      const result = await getParticipationSummary(user, tournamentId);
      if (currentGeneration !== generation.current) return;
      if (Boolean(result.participant.isUnion) !== union)
        throw new Error('Open this tournament from the correct school or union participant account.');
      setData(result);
    } catch (e) {
      if (currentGeneration === generation.current) setError(errorMessage(e));
    } finally {
      if (currentGeneration === generation.current) setBusy(false);
    }
  };
  React.useEffect(() => {
    setData(null);
    setTeamMemberCounts({});
    load();
    return () => {
      generation.current++;
    };
  }, [tournamentId, user.activeSchoolId, user.roleSessionKey]);
  React.useEffect(() => {
    if (section === 'overview' && data) load();
  }, [section]);
  const hasDraft = () => {
    const state = editor.current?.state;
    return Boolean(
      state?.itemsAccumulator &&
        state?.itemsAccumulatorInitial &&
        JSON.stringify(state.itemsAccumulator) !== JSON.stringify(state.itemsAccumulatorInitial)
    );
  };
  // Class editors already guard their own Back buttons. Also guard the workspace
  // tabs and browser Back, which otherwise bypass those buttons.
  React.useEffect(() => {
    // The union editor and recruitment page own their navigation guards.
    if ((union || page !== 'squad/manage') && !confirmationDirty) return;
    unblock.current = history.block(next => {
      if (hasDraft() || confirmationDirtyRef.current) {
        setLeave(next);
        return false;
      }
      return undefined;
    });
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (hasDraft() || confirmationDirtyRef.current) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      unblock.current?.();
      unblock.current = null;
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [page, history, confirmationDirty]);
  const open = (nextPage: string, extra: any = {}) => {
    const query = new URLSearchParams();
    query.set('tournamentId', tournamentId);
    Object.keys(extra).forEach(key => query.set(key, extra[key]));
    history.push({ pathname: `${root}/${nextPage}`, search: `?${query}`, state: returnState });
  };
  const virtualPath = participantPages(union)[page];
  const virtualLocation = { ...location, pathname: virtualPath || location.pathname };
  const destinationWithState = (target: any, state?: any) => {
    if (state === undefined) return target;
    if (typeof target !== 'string') return { ...target, state };
    const url = new URL(target, 'http://local');
    return { pathname: url.pathname, search: url.search, hash: url.hash, state };
  };
  const adaptedHistory: any = {
    ...history,
    location: virtualLocation,
    push: (target: any, state?: any) =>
      history.push(participantDestination(destinationWithState(target, state), union, tournamentId, returnState)),
    replace: (target: any, state?: any) =>
      history.replace(participantDestination(destinationWithState(target, state), union, tournamentId, returnState))
  };
  const childProps: any = {
    user,
    history: adaptedHistory,
    location: virtualLocation,
    match,
    participationReadOnly: Boolean(data?.participant.isReadOnly),
    onTeamMemberCountChange,
    participationTournament: data?.tournament
  };
  const classPage = (Component: any) => <Component {...childProps} ref={editor} />;
  if (!data)
    return (
      <main className="tpWorkspace">
        {busy ? (
          <Loader />
        ) : (
          <div className="alert alert-danger" role="alert">
            {error || 'Tournament not found'}
          </div>
        )}
        {!busy && (
          <>
            <button className="tnBtn tnBtnSecondary" onClick={load}>
              Try again
            </button>
            <button className="tnBtn tnBtnGhost" onClick={goBack}>
              Back to tournaments
            </button>
          </>
        )}
      </main>
    );
  const tournament = data.tournament;
  const individual = isIndividualTournament(tournament);
  const writable = !data.participant.isReadOnly;
  const activeTeamId = String(parse(location.search).teamId || data.firstTeam?.id || '');
  const teamMemberCount = teamMemberCounts[activeTeamId] ?? (data.teamsCount === 1 ? data.playersCount : undefined);
  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z' },
    { id: 'squad', label: 'Teams & children', icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75' },
    { id: 'events', label: 'Events', icon: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z' },
    ...(union ? [{ id: 'schools', label: 'Schools & availability', icon: 'M3 21h18M5 21V9l7-5 7 5v12M9 21v-6h6v6M9 10h.01M15 10h.01' }] : []),
    { id: 'communications', label: 'Communications', icon: 'M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z' },
    { id: 'documents', label: 'Documents', icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h8' }
  ];
  const rosterAction = () =>
    data.firstTeam && (individual || data.teamsCount === 1)
      ? open(data.playersCount ? 'squad/players' : 'squad/manage', { teamId: data.firstTeam.id })
      : open('squad');
  let content: React.ReactNode;
  switch (page) {
    case 'squad':
      content = union ? <HighLevelTournamentTeams {...childProps} ref={editor} embedded /> : classPage(TournamentTeams);
      break;
    case 'squad/manage':
      content = union ? classPage(HighLevelTournamentTeamPlayersEdit) : classPage(TournamentTeamPlayersManager);
      break;
    case 'squad/schools':
      content = union ? <HighLevelTournamentTeamPlayersEdit {...childProps} ref={editor} recruitingSchoolsPage /> : null;
      break;
    case 'squad/recruit':
      content = union ? <TournamentRecruitmentPage user={user} tournamentId={tournamentId} teamId={String(parse(location.search).teamId || '')} history={history}
        onClose={() => open('squad/manage', {teamId: String(parse(location.search).teamId || '')})}
        onImported={() => open('squad/players', {teamId: String(parse(location.search).teamId || '')})} /> : null;
      break;
    case 'squad/players':
      content = union ? classPage(HighLevelTournamentTeamPlayers) : classPage(TournamentTeamPlayers);
      break;
    case 'events':
      content = union ? classPage(HighLevelTournamentEvents) : classPage(TournamentEvents);
      break;
    case 'schools':
      content = union ? <RepresentedSchools user={user} tournamentId={tournamentId} history={adaptedHistory} readOnly={!writable} /> : null;
      break;
    case 'schools/availability':
      content = union ? <TournamentAvailability {...childProps} /> : null;
      break;
    case 'schools/notification':
      content = union ? <TournamentSchoolNotificationComposer {...childProps} audienceContext="represented" /> : null;
      break;
    case 'schools/notifications':
      content = union ? <TournamentNotifications {...childProps} embedded /> : null;
      break;
    case 'communications':
      content = <TournamentCommunications key={String(parse(location.search).communicationLevel || 'organiser')} user={user} tournament={tournament}
        organiserMode={union && parse(location.search).communicationLevel === 'schools'} participantUnionId={union && parse(location.search).communicationLevel === 'schools' ? user.activeSchoolId : undefined} />;
      break;
    case 'documents':
      content = <AdditionalInfo tournament={tournament} />;
      break;
    case 'overview':
      content = (
        <>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          <div className="tpWorkspace__kpis">
            {[
              { label: 'Teams', value: data.teamsCount, run: () => open('squad') },
              { label: 'Children', value: data.playersCount, run: rosterAction },
              { label: 'Events', value: data.eventsCount, run: () => open('events') },
              { label: 'Not assigned', value: data.unassignedCount, run: rosterAction }
            ].map(item => (
              <button className="utmOverview__kpi" key={item.label} onClick={item.run}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </button>
            ))}
          </div>
          <section className="tpWorkspace__card">
            <h2>Your next steps</h2>
            {!writable && (
              <div className="alert alert-info">
                Your participation is read-only. You can review entries, results and documents.
              </div>
            )}
            {writable && (
              <div className="tpWorkspace__next">
                <div>
                  <strong>
                    {!data.teamsCount
                      ? 'Create your tournament team'
                      : !data.playersCount
                      ? 'Add children to your squad'
                      : data.unassignedCount
                      ? `${data.unassignedCount} children have no event assignments`
                      : 'Your children have event assignments'}
                  </strong>
                  <p>
                    {individual
                      ? 'Manage your children and their event allocations.'
                      : 'Review your teams and tournament entries.'}
                  </p>
                </div>
                <button className="tnBtn tnBtnPrimary" onClick={rosterAction}>
                  {!data.teamsCount ? 'Create team' : !data.playersCount ? 'Add children' : 'Children & allocations'}
                </button>
              </div>
            )}
            {data.pendingRequests.map(request => (
              <div className="tpWorkspace__next" key={request._id}>
                <div>
                  <strong>Organiser confirmation request awaiting a response</strong>
                  {request.deadlineForAnswers && <p>Respond by {formatLondonDate(request.deadlineForAnswers)}</p>}
                </div>
                <button
                  className="tnBtn tnBtnSecondary"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    setError('');
                    try {
                      setConfirmation(await getMessageDetails(user, request._id));
                    } catch (e) {
                      setError(errorMessage(e));
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Review confirmation request
                </button>
              </div>
            ))}
            {union && (
              <div className="tpWorkspace__next">
                <div>
                  <strong>School availability</strong>
                  <p>
                    {data.availability.AVAILABLE} available · {data.availability.NOT_AVAILABLE} not available ·{' '}
                    {data.availability.PENDING} pending · {data.availability.UNREQUESTED || 0} not requested
                  </p>
                  <p>
                    {data.availabilityRequest
                      ? data.availabilityRequest.cancelled
                        ? 'Request cancelled'
                        : `Shared deadline: ${formatLondonDate(data.availabilityRequest.deadline)}`
                      : 'Availability has not been requested yet.'}
                  </p>
                </div>
                <button className="tnBtn tnBtnSecondary" onClick={() => open('schools/availability')}>
                  Review availability
                </button>
              </div>
            )}
            {!data.eventsCount && (
              <p>The organiser has not added events for your entry yet. You can continue preparing your squad.</p>
            )}
          </section>
          <section className="tpWorkspace__card">
            <h2>Dates and entry deadlines</h2>
            <p>
              Entry window: {tournament.entriesOpen ? formatLondonDate(tournament.entriesOpen) : 'Not specified'} —{' '}
              {tournament.deadlineForEntries ? formatLondonDate(tournament.deadlineForEntries) : 'Not specified'}
            </p>
            <DetailsAndBooking
              tournament={tournament}
              user={user}
              isMapVisible={mapVisible}
              onToggleMap={() => setMapVisible(!mapVisible)}
            />
          </section>
        </>
      );
      break;
    default:
      content = (
        <div className="alert alert-warning">
          This section is not available.{' '}
          <button className="tnBtn tnBtnSecondary" onClick={() => open('overview')}>
            Open overview
          </button>
        </div>
      );
  }
  if (['squad/manage', 'squad/recruit', 'squad/schools'].includes(page) && !writable)
    content = (
      <div className="tpWorkspace__card">
        <h2>Squad editing is closed</h2>
        <p>This tournament is read-only. Existing children and assignments remain available.</p>
        <button
          className="tnBtn tnBtnSecondary"
          onClick={() =>
            open('squad/players', { teamId: String(parse(location.search).teamId || data.firstTeam?.id || '') })
          }
        >
          Review children & allocations
        </button>
      </div>
    );
  return (
    <main className={`utmManagement tpWorkspace${union ? ' tpWorkspace--union' : ''}`}>
      <header className="utmManagement__header">
        <div className="utmManagement__topbar">
          <button type="button" className="utmManagement__back" onClick={goBack}>
            <span aria-hidden="true">‹</span> {union ? 'All high-level tournaments' : 'Back to tournaments'}
          </button>
          <div className="utmManagement__actions">
            {!union && writable && user.activeRole === 'ADMIN' && (
              <details className="tpWorkspace__more">
                <summary>More actions</summary>
                <button className="tnBtn tnBtnDanger" onClick={() => setWithdraw(true)}>
                  Withdraw from tournament
                </button>
              </details>
            )}
          </div>
        </div>
        <div className="utmManagement__summary">
          <div className="utmManagement__identity">
            <small>
              {data.participant.name} · {union ? 'Union' : 'School'} participation
            </small>
            <h1>{tournament.name}</h1>
            <div className="utmManagement__details">
              <span>{tournament.sport?.name}</span>
              <span>{formatLondonDate(tournament.startTime)}</span>
              <span>
                {formatAgesCompact(tournament.ages || [], resolveAgeNamingFromContext(user, tournament))} ·{' '}
                {TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[tournament.gender]}
              </span>
              <span>Organiser: {tournament.school?.name || 'Tournament organiser'}</span>
            </div>
          </div>
          <span className={`utmManagement__status ${writable ? 'is-success' : 'is-neutral'}`}>
            {!writable ? 'Read-only' : data.participant.status === 'COMPLETED' ? 'Completed' : 'Entered'}
          </span>
        </div>
        {['squad', 'events'].includes(section) && <ParticipationLimits tournament={tournament} />}
        <nav className="utmManagement__tabs" aria-label="Tournament participation sections">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={section === tab.id ? 'is-active' : ''}
              aria-current={section === tab.id ? 'page' : undefined}
              onClick={() => open(tab.id)}
            >
              <svg className="utmManagement__metaIcon" viewBox="0 0 24 24" aria-hidden="true"><path d={tab.icon} /></svg>
              {tab.label}
            </button>
          ))}
        </nav>
      </header>
      <div className="tpWorkspace__toolbar" role="toolbar" aria-label="Tournament actions">
        <button type="button" className="tnBtn tnBtnSecondary" onClick={() => setReports(true)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5" /></svg>
          Reports
        </button>
        {union && user.activeRole === 'ADMIN' && <button type="button" className="tnBtn tnBtnSecondary" onClick={() => setNotificationSettingsOpen(true)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM4 4h16v16H4z" /></svg>
          Notification recipients
        </button>}
      </div>
      {union && section === 'squad' && (parse(location.search).teamId || data.firstTeam?.id) && <nav className="utmEntries__subtabs" aria-label="Team recruitment sections">
        <button className={page === 'squad' ? 'is-active' : ''} onClick={() => open('squad')}>Teams</button>
        <button className={page === 'squad/players' ? 'is-active' : ''} onClick={() => open('squad/players', {teamId: String(parse(location.search).teamId || data.firstTeam.id)})} aria-label="Team members and allocations">Team members and allocations{typeof teamMemberCount === 'number' && <span className="tpWorkspace__tabCount">{teamMemberCount}</span>}</button>
        {writable && <><button className={['squad/manage', 'squad/schools'].includes(page) ? 'is-active' : ''} onClick={() => open('squad/manage', {teamId: String(parse(location.search).teamId || data.firstTeam.id)})}>Add from schools</button>
        <button className={page === 'squad/recruit' ? 'is-active' : ''} onClick={() => open('squad/recruit', {teamId: String(parse(location.search).teamId || data.firstTeam.id)})}>Recruit from tournaments</button></>}
      </nav>}
      {union && section === 'schools' && (
        <nav className="utmEntries__subtabs" aria-label="School participation sections">
          <button className={page === 'schools' ? 'is-active' : ''} onClick={() => open('schools')}>
            Represented schools
          </button>
          <button
            className={page === 'schools/availability' ? 'is-active' : ''}
            onClick={() => open('schools/availability')}
          >
            Availability
          </button>
          <button className={page === 'schools/notifications' ? 'is-active' : ''} onClick={() => open('schools/notifications')}>
            Notifications
          </button>
        </nav>
      )}
      {union && section === 'communications' && <nav className="utmEntries__subtabs" aria-label="Communication levels">
        <button className={parse(location.search).communicationLevel !== 'schools' ? 'is-active' : ''} onClick={() => open('communications')}>With organiser</button>
        <button className={parse(location.search).communicationLevel === 'schools' ? 'is-active' : ''} onClick={() => open('communications', {communicationLevel: 'schools'})}>With athlete schools</button>
      </nav>}
      <div
        className={`tpWorkspace__content ${
          section === 'squad' || section === 'events' || section === 'schools' ? 'tpWorkspace__embedded' : ''
        }`}
        key={`${user.activeSchoolId}:${tournamentId}:${String(parse(location.search).teamId || '')}:${union && ['squad/manage', 'squad/schools'].includes(page) ? 'squad/manage' : page}`}
      >
        {content}
      </div>
      {notificationSettingsOpen && <ParticipantNotificationSettings user={user} tournamentId={tournamentId} onClose={() => setNotificationSettingsOpen(false)} />}
      {reports && (
        <ParticipantReports user={user} tournament={tournament} union={union} onClose={() => setReports(false)} />
      )}
      <SimpleModal
        isOpen={Boolean(confirmation)}
        title="Tournament confirmation request"
        showFooter={false}
        dialogStyle={{ maxWidth: 900 }}
        onCloseClick={() => {
          if (!busy) confirmationDirty ? setLeave({ closeConfirmation: true }) : setConfirmation(null);
        }}
      >
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        {confirmation && (
          <ConfirmationRequestForm
            user={user}
            message={confirmation}
            isEditable={!busy}
            onDirtyChange={setConfirmationDirty}
            onCloseClick={() => (confirmationDirty ? setLeave({ closeConfirmation: true }) : setConfirmation(null))}
            onAcceptClick={async (fields, submission) => {
              setBusy(true);
              setError('');
              try {
                await acceptConfirmationRequest(user, confirmation.id || confirmation._id, { fields, ...submission });
                setConfirmationDirty(false);
                setConfirmation(null);
                await load();
              } catch (e) {
                setError(errorMessage(e));
              } finally {
                setBusy(false);
              }
            }}
          />
        )}
        {confirmation && (
          <button className="tnBtn tnBtnDanger" disabled={busy} onClick={() => setDeclineConfirmation(true)}>
            Decline participation request
          </button>
        )}
      </SimpleModal>
      <SimpleModal
        isOpen={declineConfirmation}
        zIndex={1080}
        title="Decline this confirmation request?"
        body="The organiser will receive your refusal."
        buttonText="Decline request"
        buttonDisabled={busy}
        onCloseClick={() => !busy && setDeclineConfirmation(false)}
        onButtonClick={async () => {
          setBusy(true);
          setError('');
          try {
            await rejectConfirmationRequest(user, confirmation.id || confirmation._id);
            setDeclineConfirmation(false);
            setConfirmationDirty(false);
            setConfirmation(null);
            await load();
          } catch (e) {
            setError(errorMessage(e));
          } finally {
            setBusy(false);
          }
        }}
      />
      <SimpleModal
        isOpen={withdraw}
        title="Withdraw from tournament?"
        body="Your school will lose its tournament place. Existing withdrawal restrictions still apply."
        buttonText="Withdraw"
        buttonCancelText="Keep participation"
        buttonDisabled={busy}
        onCloseClick={() => !busy && setWithdraw(false)}
        onButtonClick={async () => {
          setBusy(true);
          setError('');
          try {
            await unsubscribeTournament(user, tournamentId);
            setWithdraw(false);
            goBack();
          } catch (e) {
            setError(errorMessage(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
      </SimpleModal>
      <UnsavedChangesModal
        isOpen={Boolean(leave)}
        message={
          confirmationDirty
            ? 'Your confirmation answers have not been saved.'
            : 'Your squad changes have not been saved.'
        }
        onKeepEditing={() => setLeave(null)}
        onDiscard={() => {
          unblock.current?.();
          unblock.current = null;
          setConfirmationDirty(false);
          setConfirmation(null);
          setLeave(null);
          if (!leave.closeConfirmation) history.push(leave);
        }}
      />
    </main>
  );
}
